import { parseHex, blues, bivariateColor, getWidth, getHeight, setHTML } from "./Utils";
import { MIN_YEAR, MAX_YEAR, MOBILE_WIDTH, MID_WIDTH, DEBUG, MAX_WIDTH, LIGHT_GRAY, DEFAULT_COLOR, DPR, REGIONS } from "./Constants";
import GlobalScatterplotLayout from "./layouts/GlobalScatterplotLayout";
import ScatterplotLayout from "./layouts/ScatterplotLayout";
import MapLayout from "./layouts/MapLayout";
import Centroid from "./layouts/Centroid";
import Layout from "./layouts/Layout";
import * as PIXI from 'pixi.js'; // use pixi@4.8.8
import * as d3 from "d3";
import _ from 'underscore';

export default class LayoutManager {
	constructor(nodes, container, model, width, height, labelSelector, app) {
		this.app = app;
		this.nodes = nodes;
		this.container = container;
		this.model = model;
		this.filters = d3.set();
		this.hover_active = -1;
		this.width = width; //this.app.renderer.width/window.devicePixelRatio
		this.height = height;
		this.labelSelector = labelSelector;
		this.axes = this.app.axes;
		this.scale = this.app.scale;
		this.labels = () => this.labelSelector.querySelectorAll('.label');
		this.timer = d3.timer( () => this.timer.stop());
		this.zoom_timer = d3.timer(() => this.zoom_timer.stop());
		this.pulse_timer = d3.timer(() => this.pulse_timer.stop());
		this.simulation;
		this.spares = this.app.spares_container;
		this.count = this.nodes.length;
		this.current = "map";
		this.slice = Infinity;
		this.key;
		this.d3_empty = d3.selectAll('foo');
		this.current_index = 'map_Infinity';
		this.layouts = new WeakMap();
		this.delay = 320;
		this.max_size = Math.min(MAX_WIDTH, this.width);
		this.grid_size = this.width < MOBILE_WIDTH  || this.aspectRatio == [1,1] ? this.max_size : this.app.aspectRatio == [2.1, 1] ? this.max_size/2 - this.max_size/16 : this.max_size/1.85;///2 - this.max_size/12;
		this.alpha = .8;  // .58 for higher framerates - maybe use a scale
		this.velocityDecay = .7;
		this.alphaTarget = 0;
		this.alphaDecay = .001;
		this.ratio;
		this.old_ratio = null;
		this.needs_render = false;
		this.mutex = false;
		this.search = undefined;
		this.abortController = new AbortController();
		this.initialize();
	}

	halt() {
		this.mutex = false;
		this.pulse_timer.stop();
		this.timer.stop();
		this.zoom_timer.stop();
	}

	abort() {
		this.halt();
		// only works with Chome 88+
		this.abortController.abort();
		this.app.axes.removeEventListener(
			this.app.isTouch ? 'touchmove' : 'mousemove',
			this.debouncedMouseMoveListener, 
			{ signal: this.abortController.signal } 
		);
		this.app.axes.removeEventListener("mouseover", this.hitboxOverListener.bind(this), { signal: this.abortController.signal } );
		this.app.axes.removeEventListener("mouseout", this.hitboxOutListener.bind(this), { signal: this.abortController.signal } );
		this.app.axes.removeEventListener("mousedown", this.axesListener.bind(this), { signal: this.abortController.signal } );
	}

	resetFilters() {
		if (typeof this.filters != 'undefined') this.filters.clear();
		this.filters = d3.set(Object.entries(REGIONS).map(d=> d[1]));
	}

	initialize() {
		this.filters = this.resetFilters();
		this._center = this.center();

		this.nested = d3.nest()
		 .key(function(d){ return d.iso;})
		 .entries(this.nodes)
		 .sort(function(a,b){ return d3.descending(+a.key, +b.key);})
		 .sort(function(a,b){ return d3.descending(a.values.length, b.values.length);});
		this.nested_full = this.nested;

		this.top25 = [...this.nested.slice(0,25).map(d=> d.key)];
		this.topNodes = this.top25Nodes();
		this.dim = Math.ceil(Math.pow(this.nested[0].values.length, .5));
		this.spriteWidth = this.nodes[0].width;
		this.grid_order = new Array(this.dim * this.dim);
		// var t0 = performance.now();
		for (let i = 0; i < this.nested.length; i++ ) {
			let d = this.nested[i];
			d.values = _.shuffle(d.values);
			let gri = this.model.getGRI(d.key);
			let shi = this.model.getSHI(d.key);
			let score_tint = bivariateColor(gri, shi);
			for (let j = 0; j < d.values.length; j++ ) {
				let e = d.values[j];
				e.grid_order = j;
				if (typeof this.grid_order[j] == 'undefined') {
				 this.grid_order[j] = [];
				}
				this.grid_order[j].push([e.id, parseHex(blues(Math.floor(((j/d.values.length)) * 9))),score_tint , score_tint, score_tint]);
			}
		}
		this.grid_order = this.grid_order.filter(function(d) { return typeof d != 'undefined'; });
		this.layouts["map_Infinity"] = new MapLayout("map", Infinity, this.nodes);
		this.symlog = d3.scaleSymlog([0, this.dim], [.4, .4]);
		this.linear = d3.scaleLinear([0,this.dim**2], [1, .7]);
		this.hash = {};
		for(let i=0; i < this.nodes.length; i++){
			if(typeof this.hash[this.nodes[i].iso] == 'undefined'){
				this.hash[this.nodes[i].iso] = i;
			}
		}

		this.hash_values = Object.values(this.hash);
		this.initializeSimulation();
		this.listen();
	}
	_ () {
		return this.layouts[this.current_index];
	}

	bitWipe(grid_order, type=1) {
		let x = grid_order % this.dim;
		let y = Math.floor(grid_order/this.dim);
		function inner(x,y, z=350) {
			let _y = ~y;
			return ((x ^ _y) & ((y - z) >> 3 )) ** 2;
		}
		if (type ==2){
			return (( x*x*y >> 11) & 1) == 1 ? .75 : .8;
		}
		return (((inner(x,y)) >> 11 ) & 1) == 1 ? .8 : .75;
	}

	initializeSimulation() {
		this.simulation = d3.forceSimulation(this.nodes)
			.force("x", this.layouts[this.current_index].x_force.strength(0) )
			.force("y", this.layouts[this.current_index].y_force.strength(0) )
			.force("charge", null)
			.force("collide", null)
			.force("center", null)
			.velocityDecay(this.velocityDecay);
		this.simulation.alpha(this.alpha).stop();
	}
	passThrough() {
		if (this.current == 'centroid') {
			for (let i=0; i < this.nodes.length; i++) {
				if (this.nodes[i].iso == this.layouts[this.current_index].key) {
					this.nodes[i].x = this.layouts[this.current_index].x;
					this.nodes[i].y = this.layouts[this.current_index].y;
				}
			}
		} else if (this.current == 'global_scatter'){
			for (let i=0; i < this.nodes.length; i++) {
				this.nodes[i].x = this.layouts[this.current_index]._coordinates_x(this.nodes[i].iso);
				this.nodes[i].y = this.layouts[this.current_index]._coordinates_y(this.nodes[i].iso);
			}
		} else {
			for (let i=0; i < this.nodes.length; i++) {
				this.nodes[i].x = this.layouts[this.current_index]._coordinates_x(this.nodes[i].id);
				this.nodes[i].y = this.layouts[this.current_index]._coordinates_y(this.nodes[i].id);
			}
		}
		this.needs_render = true;
	}

	filterRegions(){
		let values = this.filters.values().map(d=> parseInt(d));
		for(let i=0; i < this.nodes.length; i++){
			this.nodes[i].alpha = ~values.indexOf(this.nodes[i].tint) ? 1 : 0;
		}
		for(let i=0; i < this.app.spares_container.children.length; i++){
			this.app.spares_container.children[i].alpha = ~values.indexOf(this.app.spares_container.children[i].tint) ? 1 : 0;
		}
	}


	updateSimulationConfig() {
		if (this.app._ticker.FPS > 45) {
			this.alpha = .58;  // .58 for higher framerates - maybe use a scale
			this.velocityDecay = .7;
			this.alphaTarget = 0;
			this.alphaDecay = .001;
		} else {
			this.alpha = .85;  // .58 for higher framerates - maybe use a scale
			this.velocityDecay = .7;
			this.alphaTarget = 0;
			this.alphaDecay = .009;
		}
	}

	updateSimulation(alpha=this.alpha, velocityDecay=this.velocityDecay) {
		if (typeof this.app._ticker != 'undefined') {
			this.simulation.stop();
			if (this.app.isWebGL && (this.app._ticker.FPS > this.app.fps_threshold || (this.app.step == 99 && this.current == 'scatter')) ) {
				this.updateSimulationConfig();
				this.simulation
					.force("x", this.layouts[this.current_index].x_force )
					.force("y", this.layouts[this.current_index].y_force );
					if (~['scatter', 'centroid', 'map'].indexOf(this.current)){
						this.layouts[this.current_index].x_force.strength(1);
						this.layouts[this.current_index].y_force.strength(1);
					} else if (this.current == 'demo' || this.current == 'global_scatter'){
						this.layouts[this.current_index].x_force.strength(d => this.symlog((Math.floor(d.grid_order/this.dim))/100 || 0));
						this.layouts[this.current_index].y_force.strength(d => this.symlog((d.grid_order|| 0) % this.dim) );
					} else {
						this.layouts[this.current_index].x_force.strength(d => this.linear(d.grid_order || 0));
						this.layouts[this.current_index].y_force.strength(d => this.linear(d.grid_order || 0));
					}
				this.simulation.alpha(alpha).velocityDecay(velocityDecay).alphaDecay(this.alphaDecay).alphaTarget(this.alphaTarget).on('end', ()=> this.needs_render = true ).restart();
			} else {
				 this.passThrough();
			}
		}
	}

	updateLabels(key=undefined, type="map") {
		this.labelSelector.innerHTML = ``; // phpcs:ignore
		this.axes.innerHTML = ``; // phpcs:ignore
		this.scale.innerHTML = ``; // phpcs:ignore
		if (~['scatter', 'global_scatter'].indexOf(type)) {
			this.axes.setAttribute('data-layout', type);
			this.axes.setAttribute('data-region', JSON.stringify(Object.values(REGIONS)));
			this.axes.appendChild(this.layouts[this.current_index].axes.cloneNode(true));
			this.labelSelector.appendChild(this.layouts[this.current_index].labels.cloneNode(true));

		} else if (typeof key == 'undefined' || ~["map", "centroid"].indexOf(type)){
			this.labelSelector.appendChild(this.layouts[this.current_index].labels.cloneNode(true));
		} else {
			let _labels = document.createDocumentFragment();
			if (typeof key == 'string') {
				let labels = this.layouts[this.current_index].labels.cloneNode(true).querySelectorAll(`[id^='label--${key}']`);
				for( let i = 0; i < labels.length; i++) {
					_labels.appendChild(labels[i]);
				}
				this.labelSelector.appendChild(_labels);
			} else {
				let labels = this.layouts[this.current_index].labels.cloneNode(true).querySelectorAll(`${key.map( k => `[id^='label--${k}']`).join(',')}`);
				for( let i = 0; i < labels.length; i++) {
					_labels.appendChild(labels[i]);
				}
				this.labelSelector.appendChild(_labels);
			}
		}
		//draw scales for grid Layouts
		if (~['gri', 'shi'].indexOf(type) && ~[7,8].indexOf(this.app.step)) {
			this.scale.appendChild(this.layouts[this.current_index].scale.cloneNode(true));
		}
	}

	throttle() {
		return this.app._ticker.fps < this.app.fps_threshold || this.app.is_navigating ? false : true;
	}

	create(type="pop", slice=Infinity, key=undefined, offset_x=0, offset_y=0, min_year=MIN_YEAR, order=undefined) {
		// grid dimensions should be most constrained value
		if (DEBUG) console.log('type', type, 'slice', slice, 'key', key, 'offset_x', offset_x, 'offset_y', offset_y, 'min_year', min_year, 'order', order);
		let _width = getWidth();
		let _height = getHeight();
		//get most constrained dimension
		let grid_dimension =  Math.min(this.grid_size, _width, _height);
		if (type == "global_scatter") {
			let width = _width/_height >= 2 && ~['156', '356'].indexOf(key) ? grid_dimension - 10 : _width > MID_WIDTH ? grid_dimension - 10 : _width > MOBILE_WIDTH ? grid_dimension : grid_dimension;

			this.layouts['global_scatter_' + min_year] = new GlobalScatterplotLayout(type, this.nested_full, this.nested_full.length, this.model,width, width, offset_x, offset_y, min_year);
		} else if (type == "scatter") {
			let width = _width/_height >= 2 && ~['156', '356'].indexOf(key) ? grid_dimension - 10 : _width > MID_WIDTH ? grid_dimension - 10 : _width > MOBILE_WIDTH ? grid_dimension : grid_dimension;
			let nodes = this.nested_full.filter( d => d.key == key)[0].values;
			if (nodes.length < (MAX_YEAR - MIN_YEAR + 2)) nodes = nodes.concat(this.spares.children);
			this.layouts[type + '_' + key] = new ScatterplotLayout(type, nodes, nodes.length, this.model,width, width, offset_x, offset_y, min_year);
		} else if (type == "centroid") {
			this.layouts[type + '_' + key] = new Centroid(type, key, offset_x, offset_y);
		} else if ( typeof order != "undefined") {
			let width = _width > MID_WIDTH ? grid_dimension - 50 : _width > MOBILE_WIDTH ? grid_dimension - 50 : grid_dimension;
			this.layouts[type + '_order'] = new Layout(type, slice, this.nested, this.count, this.model, width, width, this.spriteWidth, this.dim, offset_x, offset_y, order);
		} else if (typeof this.layouts[type + '_' + slice] == 'undefined') {
			this.layouts[type + '_' + slice] = new Layout(type, slice, this.nested, this.count, this.model, grid_dimension, grid_dimension, this.spriteWidth, this.dim);
		}
	}

	layout(type="pop", slice=Infinity, key=undefined, offset_x=0, offset_y=0, min_year=MIN_YEAR, max_year=MAX_YEAR, order=undefined) {
		this.timer.stop();
		this.simulation.stop();
		// if there is an ongoing animation controlled by the current layout, stop it
		if(typeof this._().timer !== 'undefined') this._().timer.stop();

		if (this.current == "scatter") {
			this.layouts[this.current_index].timer.stop();
			this.updateLabels(key, type);
			this.resizeNodes();
			if (type != "scatter"){
				document.querySelector('#axes').classList.remove('active');
			}
		}
		let old_key = this.key;
		let old_current = this.current;
		this.current = type;
		this.slice = slice;
		this.key = key;
		this.current_index = `${type}_${'global_scatter' == type ? min_year : ~["scatter", "centroid"].indexOf(type) ? key : typeof order != 'undefined' ? 'order' : slice }`;

		if (type != "map" && (typeof this.layouts[this.current_index] == 'undefined' || type != 'global_scatter')) {
			if (DEBUG) console.log(`${this.current_index} created at ${Date.now()}`);
			this.create(type, slice, key, offset_x, offset_y, min_year, order);
		}

		if (type == 'scatter') {
			this.isolateCountry(key);
			let nodes = this.nested_full.filter( d => d.key == key)[0].values;
			if (nodes.length < MAX_YEAR - MIN_YEAR + 2) this.spares.renderable = true;
			this.simulation.nodes(this.layouts[this.current_index].nodes);
		}

		if (!~["scatter", "centroid", "global_scatter"].indexOf(this.current)) {
			this.layouts[this.current_index].offset_x = offset_x;
			this.layouts[this.current_index].offset_y = offset_y;
		}
		if (type === 'global_scatter') {
			if (old_current != 'global_scatter') {
				for (let i=0; i< this.nested.length; i++){
					
						this.nested[i].values[0].scale.set(this._().maxSize);

				}
				this.simulation.nodes(this.nodes);
				this.updateSimulation(.85, .7);
				this.updateLabels(key, type);
				document.querySelector('#axes').classList.add('active');
			}
			this.app.spares_container.renderable = false;
			this.layouts[this.current_index].layout(old_current !='global_scatter' ? true : false);
			this.resetFilters();
		}

		if (type == 'scatter' ) {
			// South Sudan
			if ( min_year > MIN_YEAR  && key != '728') {
				this.layouts[this.current_index].layout(true);
				this.updateSimulation(0.8, 0.7);
				this.updateLabels(key, type);
				document.querySelector('#axes').classList.add('active');
			} else {
				this.layouts[this.current_index].year = min_year;
				this.layouts[this.current_index].layout(this.throttle());
				this.updateLabels(key, type);
				this.updateSimulation(0.85, 0.7);
				document.querySelector('#axes').classList.add('active');
			}
			let period = max_year - min_year;
			if ( period > 0 ) {
				this.timer = d3.interval( elapsed => {
					this.layouts[this.current_index].year++;
					this.layouts[this.current_index].layout(this.throttle());
					this.updateLabels(key, type);
					this.updateSimulation(0.85, 0.7);
					if ( elapsed  >= this.delay * period || this.current != "scatter" ){
						this.timer.stop();
					}
				}, this.delay );
			}
		} else if( typeof order != "undefined") {
			this.updateLabels(key, type);
			this.updateSimulation();
		} else {
			this.updateLabels(key, type);
			this.updateSimulation();
		}
	}

	resizeNodes(){
		let country = this.nested_full.filter((d) => d.key == this.key)[0];
		for (let i =0; i < country.values.length; i++ ){
			country.values[i].scale.x = country.values[i].scale.y =  country.values[i].baseScale;
		}
	}

	pulse(key='840', color1='#C9DEEE', color2='#71B2D6', alpha1=1, alpha2=1, invert=false) {
		this.pulse_timer.stop();
		let country = this.getCountryNodes(key, invert);
		let color_scale = d3.scaleLinear([0,1], [color1, color2]);
		let alpha_scale = d3.scaleLinear([0,1], [alpha1, alpha2]);
		this.mutex = true;
		this.pulse_timer = d3.timer( elapsed => {
			let t = d3.easeCubic(elapsed/250);
			let factor = 0 * (1-t) + 1 * t;
			factor = +factor.toFixed(2);
			let color = parseHex(d3.color(color_scale(factor)).hex());
			let alpha = alpha_scale(factor);
			for ( let i = 0; i < country.length; i++ ) {
				country[i].tint = color;
				country[i].alpha = alpha;
			}
			if (t >= 1) {
				this.mutex = !this.mutex;
				this.pulse_timer.stop();
			}
		});
	}

	renderAll(){
		for (let i=0; i < this.nodes.length; i++){
			this.nodes[i].visible = true;
		}
	}

	renderNone(){
		for (let i=0; i < this.nodes.length; i++){
			this.nodes[i].visible = false;
		}
	}

	renderFirst() {
		this.renderNone();
		for(let i=0; i < this.hash_values.length; i++) {
			this.nodes[this.hash_values[i]].visible = true;
		}
	}

	resetScaleFirst(){
		for(let i=0; i < this.hash_values.length; i++){
			this.nodes[this.hash_values[i]].scale.set(this.nodes[this.hash_values[i]].baseScale);
		}
	}
	resetScaleAll(){
		for(let i=0; i < this.nodes.length; i++){
			this.nodes[i].scale.set(this.nodes[i].baseScale);
		}
	}

	globalScatterScaleFirst(){
		for(let i=0; i < this.hash_values.length; i++){
			this.nodes[this.hash_values[i]].scale.set(this.layouts[this.current_index].minSize);
		}
	}

	score_tint(type="score", velocity=0, key=undefined) {
		const types = {
			score: 2,
			gri: 3,
			shi: 4
		};
		this.mutex = true;
		velocity = Math.max(0, 170 - 34 * velocity);
		this.d3_empty
			.data(this.grid_order)
			.enter()
			.transition()
			.delay( (d,i) => {
					let t = d3.easeCircleOut(i/this.grid_order.length);
					return  velocity * (1-t) + 4 * velocity * t;
				})
			.on('end', (d,i) => {
				if (d) {
					for (let j = 0; j < d.length; j++ ) {
						this.nodes[d[j][0]].alpha = 1;
						if (typeof key != "undefined") {
							if (~key.indexOf(this.nodes[d[j][0]].iso)){
								this.nodes[d[j][0]].tint = d[j][types[type]];
							} else {
								this.nodes[d[j][0]].tint = LIGHT_GRAY;
							}
						} else {
							this.nodes[d[j][0]].tint = d[j][types[type]];
						}
					}
				}
				if (i == this.grid_order.length - 1 ) {
					this.mutex = !this.mutex;
				}
			});
	}

	center() {
		//let pos = this.container.getGlobalPosition();
		return [this.app.canvas.width/(2 * DPR), this.app.canvas.height/(2 * DPR)];
	}

	getCountryNodes(key, invert=false) {
		if (invert) {
			return typeof key == 'object' ? this.nodes.filter(d => !~key.indexOf(d.iso)) : this.nodes.filter(d => d.iso != key);
		} else {
			return typeof key == 'object' ? this.nodes.filter(d => ~key.indexOf(d.iso)) : this.nodes.filter(d => d.iso == key);
		}
	}

	top25Nodes() {
		return this.getCountryNodes(this.top25);
	}

	getCentroid(key, grid=false, offset=true, layout=this.current_index) {
		if ( layout == "map_Infinity") {
			return this.getMapCentroid(key, offset);
		} else if (grid) {
			return this.getGridCentroid(key, offset, layout);
		} else {
			return this.getCountryNodes(key)
			.reduce((centroid, d, i, a) => {
				let ptr = this.layouts[layout].coordinates[d.id];
				return [centroid[0] + ptr.x/a.length, centroid[1] + ptr.y/a.length];
			}, [0,0]);
		}
	}

	getMapCentroid(key, offset) {
		return this.getCountryNodes(key)
		.reduce((centroid, d, i, a) => {
			let ptr = this.layouts['map_Infinity'];
			return [centroid[0] + ptr._coordinates_x(d.id, offset)/a.length, centroid[1] + ptr._coordinates_y(d.id, offset)/a.length];
		}, [0,0]);
	}

	getGridCentroid(key, offset, layout=this.current_index) {
		let first = this.nodes.filter((d) => d.iso == key)
		.sort(function(a,b){ return d3.ascending( a.grid_order, b.grid_order)})[0];
		let ptr = this.layouts[layout];
		let centroid = new PIXI.Point(ptr._coordinates_x(first.id, true) + ptr.gridCell/2, ptr._coordinates_y(first.id, true) + ptr.gridCell/2);
		let _centroid = this.app.app.stage.toLocal(centroid, this.point_of_certainty, null, true);
		return [ptr._coordinates_x(first.id, offset) + ptr.gridCell/2, ptr._coordinates_y(first.id, offset) + ptr.gridCell/2];
	}

	resetColor(color=DEFAULT_COLOR) {
		for (let i = 0; i < this.nodes.length; i++ ) {
			this.nodes[i].tint = color;
			this.nodes[i].alpha = 1;
		}
	}

	regionColor() {
		// make North Korea gray
		for (let i = 0; i < this.nodes.length; i++ ) {
			this.nodes[i].tint = this.nodes[i].iso === '408' ? LIGHT_GRAY : REGIONS[this.model.getRegion(this.nodes[i].iso)];
			this.nodes[i].alpha = 1;
		}
		for (let i=0; i < this.app.spares_container.children.length; i++){
			this.app.spares_container.children[i].tint = REGIONS[this.model.getRegion(this.app.spares_container.children[i].iso)];
			this.app.spares_container.children[i].alpha = 1;
		}
	}

	isolateCountry(key) {
		if (typeof key == 'undefined') return;
		for (let i = 0; i < this.nodes.length; i++ ) {
			if (typeof key === 'string') {
				if ( this.nodes[i].iso !== key) {
					this.nodes[i].alpha = 0;
				}
			} else if (typeof key === 'object'){
				if ( !~key.indexOf(this.nodes[i].iso)) {
					this.nodes[i].alpha = 0;
				}
			}
		}
		for (let i=0; i < this.app.spares_container.children.length; i++){
			this.app.spares_container.children[i].alpha = this.app.spares_container.children[i].iso  !== key ? 0 : this.app.spares_container.children[i].alpha;
		}
	}

	setAlpha(alpha=1){
		for (let i = 0; i < this.nodes.length; i++ ) {
			this.nodes[i].alpha = alpha;
		}
		for (let i = 0; i < this.spares.length; i++) {
			this.spares[i].alpha = alpha;
		}
	}

	setAlphaFirst(alpha=1, key=undefined) {
		if(typeof key == 'undefined') {
			for (let i =0; i < this.nested_full.length; i++){
				this.nested_full[i].values[0].alpha = alpha;
			}
		} else {
			for (let i =0; i < this.nested_full.length; i++){
				if (this.nested_full[i].key == key) {
					this.nested_full[i].values[0].alpha = alpha;
				}
			}
		}
	}

	countryColor(key, color=DEFAULT_COLOR) {
		let country = this.getCountryNodes(key);
		for ( let i = 0; i < country.length; i++ ) {
			country[i].tint = color;
		}
	}

	resizeLabels(zoom=1) {
		let labels = this.labels();
		let factor = Math.ceil(this.app.aspectRatio[0] * this.app.ratio)/Math.ceil(this.app.aspectRatio[0] * this.app.old_ratio) * zoom;
		if (!labels.length > 0 ) return;
		for(let i = 0; i < labels.length; i++) {
			labels[i].style.cssText = `top:${factor * parseFloat(labels[i].style.top.slice(0, -2)) /*parseFloat(labels[i].style.top.slice(0, -2))*/ }px;
				left:${factor  * parseFloat(labels[i].style.left.slice(0, -2))}px;
				width:${factor * parseFloat(labels[i].style.width.slice(0, -2))}px;
				height:${factor * parseFloat(labels[i].style.height.slice(0, -2))}px;
			`;
		}
		this.labelFactor = parseFloat(labels[0].style.top.slice(0, -2))/parseFloat(labels[0].dataset.top);
	}

	sort(key){
		//this.app.container.children.sort((x,y) => x.iso == key ? 1 : y.iso == key ? -1 : 0);
		//this.app.spares_container.children.sort((x,y) => x.iso == key ? 1 : y.iso == key ? -1 : 0);
	}

	highlight(key, recolor=true) {
		if (typeof this.pulse_timer != 'undefined') this.pulse_timer.stop();
		let country = this.nested.filter(d=>d.key == key)[0];
		this.app.container.removeChild(...country.values);
		this.app.country.addChild(...country.values);
		this.pulse_timer = d3.timer ( elapsed => {
			let t = d3.easeBounceOut(elapsed/400);
			let scale = this._().maxSize * ( 1 - t ) + this._().maxSize * 1.75 * t;
			for (let i=0; i< this.nested.length; i++){
				if ( this.nested[i].key == key){
					this.nested[i].values[0].scale.set(scale);
				} else {
					this.nested[i].values[0].scale.set(this._().maxSize);
				}
			}
			if ( elapsed >= 400 ){
				this.pulse_timer.stop();
			}
		});
		if (recolor) {
			for ( let i=0; i < this.nodes.length; i++ ) {
			 	if (this.nodes[i].iso != key ){
					this.nodes[i].tint = LIGHT_GRAY;
				} else {
					this.nodes[i].tint = REGIONS[this.model.getRegion(key)];
				}
			}
		}
	}

	// this doesn't use the offsets for some reason, but label repositioning does ?
	zoomTo(start, end, x1, y1, x2, y2, velocity, offset_x=0, offset_y=0, callback=null, labelsOff=false ) {
		if( start === end && x1 === x2 && y1 === y2 ){
			if (callback != null) {
				callback();
				this.needs_render = true;
			}
			return;
		}
		if (DEBUG) console.log(start, end, x1, y1, x2, y2, velocity);
		// setTransform (x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY)
		this.zoom_timer.stop();
		let center = this.center();
		//let p = this.container.getGlobalPosition();
		//let center = [c[0] - p.x, c[1] - p.y ];
		this.zoom_timer = d3.timer( elapsed => {
			let t = d3.easeCubic(elapsed/Math.max(170, 750 - 75 * velocity));
			let x = x1 * (1 - t) + x2 * t;
			let y = y1 * (1 - t) + y2 * t;
			let scale = start * ( 1 - t ) + end * t;
			if (start < end) {
				this.container.setTransform(center[0] - (x2 - x ) *  (end - start) * t , center[1] - (y2 - y) * (end - start) * t ,scale,scale,0,0,0, x , y );
			} else if ( start > end ){
				this.container.setTransform(center[0] + (x2 - x ) *  (start - end) * t , center[1] + (y2 - y) * (start - end) * t ,scale,scale,0,0,0, x , y );
			} else {
				this.container.setTransform(center[0], center[1],scale,scale,0,0,0, x, y);
			}
			if (t >= 1) {
				if ( (start != end || x1 != x2 || y1 != y2) && !labelsOff) {
					this.resizeLabels(end/start);
					this.labelSelector.style.cssText = `transform: translate(${((x1 * start) * ( 1 - t ) - x2  * end * t + center[0]) + offset_x * end}px, ${((y1 * start) * ( 1 - t ) - y2  * end * t + center[1]) + offset_y * end}px);`;
					if (end > 1) {
						this.labelSelector.classList.add("zoom");
					} else {
						this.labelSelector.classList.remove("zoom");
					}
				}
				this.zoom_timer.stop();
				if (callback != null) {
					callback();
					this.needs_render = true;
				}
			}
		}, 1);
	}

	countryTooltip(iso, year=MAX_YEAR){
		setHTML( `<h4>${this.model.getName(iso)} <span class="year">${year}</span></h4>
		<div style="display:flex;font-size:13px;margin-bottom:5px;">
			<div style="width:15%;margin-right:15px;">GRI</div>
			<div style="width:auto;align-self:flex-end;">
				${this._().x_labels(this.model.getGRI(iso, year))}
				(${d3.format(".1f")(this.model.getGRI(iso, year))})
			</div>
		</div>
		<div style="display:flex;font-size:13px;">
			<div style="width:15%;margin-right:15px;">SHI</div>
			<div style="width:auto;align-self:flex-end;">
				${this._().y_labels(this.model.getSHI(iso, year))}
				(${d3.format(".1f")(this.model.getSHI(iso, year))})
				</div>
		</div>`, `.${this.app.tooltip.className}` );
	}

	mouseMoveListener(e) {
		if ( !e.target.matches( 'svg' ) ) return;
		if ( this.current == 'global_scatter' ) {
			let graphic = document.querySelector( '#graphic' );
			var rect = e.target.getBoundingClientRect();
			var x = +e.clientX - rect.left; //x position within the element.
			var y = +e.clientY - rect.top;  //y position within the element.
			var radius = 9;
			x = x - this._().margin.left;
			this.search = undefined;
			this.search = this._().search(x, y, radius, (d) => { return this.filters.has(REGIONS[this.app.model.getRegion(d[0])].toString())});
			if (typeof this.search != 'undefined' && this.search.length > 0) {
				if (this.hover_active !== this.search[0][0] ){
					this.hover_active = this.search[0][0];
					this.highlight(this.hover_active, false);
					this.app.tooltipContainer.classList.add('active');
					document.body.style.cursor = this.app.step == 98 ? "pointer" : "default";
					let year = this._().year;
					this.countryTooltip(this.search[0][0], year);
				}
				this.app.tooltip.setAttribute('style',
					`top:${e.clientY}px;
					left:${e.clientX - ( graphic.offsetLeft + graphic.offsetParent.offsetLeft )}px;
					font-size:14px;
					padding:10px;
					transform:translate(20px, 0px);
				`);

			} else {
				document.body.style.cursor = "default";
				this.app.tooltipContainer.classList.remove( 'active' );
				if (this.hover_active !== -1) {
					// halt any currently active animations
					this.pulse_timer.stop();
					for (let i=0; i < this.nested.length; i++){
						if ( this.nested[i].key == this.hover_active) {
							this.nested[i].values[0].scale.set(this._().maxSize);
						}
					}
					let country = this.nested.filter(d=>d.key == this.hover_active)[0];
					this.app.country.removeChild(...country.values);
					this.app.container.addChild(...country.values);
					this.hover_active = -1;
				}
			}
		}
	}
	hitboxOverListener(e) {
		if (!e.target.matches('.hitbox')) return;
		e.stopPropagation();
		let graphic = document.querySelector('#graphic');
		this.app.tooltip.setAttribute('style',
			`top:${e.clientY}px;
			left:${e.clientX - ( graphic.offsetLeft + graphic.offsetParent.offsetLeft )}px;
			font-size:14px;
			padding:10px;
			transform:translate(20px, 0px);
		`);
		this.app.tooltipContainer.classList.add('active');
		if (this.current == 'scatter' || this.current == 'global_scatter') {
			this.countryTooltip(e.target.dataset.iso, e.target.dataset.year);
		}
	}
	hitboxOutListener(e) {
		if(!e.target.matches('.hitbox')) return;
		e.stopImmediatePropagation();
		this.app.tooltipContainer.classList.remove('active');
	}
	axesListener(e) {
		console.log('fire', e);
		if(e.target.matches('.hitbox') && this.app.step == 98){
			this.app.tooltipContainer.classList.remove('active');
			this.app.dropdown.dropdown('set selected', e.target.dataset.iso);
		} else if (e.target.matches('.legend .region')){
			this.axes.setAttribute('data-region', e.target.dataset.region);
			if (this.filters.has(e.target.dataset.region)) {
				e.target.classList.remove('active');
				this.filters.remove(e.target.dataset.region);
			} else {
				e.target.classList.add('active');
				this.filters.add(e.target.dataset.region);
			}
			this.filterRegions();
		} else if (e.target.matches('.title span')){
			this.app.handleDropdown();
			this.app.dropdown_icon.classList.remove('clear');
		} else if (e.target.matches('svg') && this.current == "global_scatter" && this.app.step == 98 && this.hover_active !== -1 ) {
			this.app.tooltipContainer.classList.remove('active');
			document.body.style.cursor = "default";
			this.app.dropdown.dropdown('set selected', this.hover_active);
		}
	}

	listen() {
		this.debouncedMouseMoveListener = _.debounce(this.mouseMoveListener.bind(this), 10 );
		
			this.app.axes.addEventListener(
				 this.app.isTouch ? 'pointerdown' : 'mousemove',
				this.debouncedMouseMoveListener, 
				{ signal: this.abortController.signal } 
			);
		

		this.app.axes.addEventListener("mouseover", this.hitboxOverListener.bind(this), { signal: this.abortController.signal } );
		this.app.axes.addEventListener("mouseout", this.hitboxOutListener.bind(this), { signal: this.abortController.signal } );
		this.app.axes.addEventListener('mousedown', this.axesListener.bind(this), { signal: this.abortController.signal } );
	}
}
